import * as React from "react";
import { Fragment, useState } from "react";

import { IoCloseOutline } from "@react-icons/all-files/io5/IoCloseOutline";
import { Dialog, Transition } from "@headlessui/react";
import { Text } from "@uikit";
import { Airport, Image } from "@kalabo/golfsavers-common";
import GoogleMapReact from "google-map-react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import { CourseSimple } from "@kalabo/golfsavers-common";
import StarRating from "@components/starRating";

interface IProps {
  longitude: number;
  courses: CourseSimple[];
  airports?: Airport[];
  latitude: number;
  images: Image[];
}

const Marker = () => (
  <img
    alt="Map Marker"
    src={`${process.env.GATSBY_IMAGE_PREFIX}/website/map-marker.png`}
    className="w-[30px] "
    style={{ transform: "translate(-50%, -100%)" }}
  />
);

interface IAirportMarkerProps {
  airport: any;
}

const AirportMarker = ({ airport }: IAirportMarkerProps) => (
  <div className="group relative flex flex-col items-center cursor-pointer">
    <img
      alt="Map Marker"
      src={`${process.env.GATSBY_IMAGE_PREFIX}/website/airport-map-marker.png`}
      className="w-[30px] "
      style={{ transform: "translate(-50%, -50%)" }}
    />
    <div
      className={`bg-white gap-2 shadow-md rounded-full px-4 py-2 text-12 font-[600] font-poppins  group-hover:z-[9999] group-hover:flex hidden`}
      style={{ transform: "translate(0, -50%)" }}
    >
      <span className="max-w-[150px] sm:max-w-none  whitespace-nowrap text-ellipsis overflow-hidden">
        {airport.name.en}
      </span>
    </div>
  </div>
);

interface ICourseMarkerProps {
  course: CourseSimple;
  selected: boolean;
}
const CourseMarker = ({ course, selected }: ICourseMarkerProps) => (
  <div className="group relative flex flex-col items-center">
    {selected ? (
      <img
        alt="Map Marker"
        src={`${process.env.GATSBY_IMAGE_PREFIX}/website/map-marker-selected.png`}
        className="w-[30px] "
        style={{ transform: "translate(-50%, -50%)" }}
      />
    ) : (
      <img
        alt="Map Marker"
        src={`${process.env.GATSBY_IMAGE_PREFIX}/website/golf-map-marker.png`}
        className="w-[30px] "
        style={{ transform: "translate(-50%, -50%)" }}
      />
    )}
    <div
      className={`bg-white  gap-2 shadow-md rounded-full px-4 py-2 text-12 font-[600] font-poppins group-hover:z-[9999] group-hover:flex hidden ${
        selected && "!hidden"
      }`}
      style={{ transform: "translate(0, -50%)" }}
    >
      <span className="max-w-[150px] sm:max-w-none  whitespace-nowrap text-ellipsis overflow-hidden">
        {course.name.en}
      </span>
      <StarRating
        mode="small"
        rating={course?.reviews?.average_rating}
        size="tiny"
      />
    </div>
  </div>
);

function mapOptionsCreator(map) {
  return {
    zoomControlOptions: {
      position: map.ControlPosition.TOP_LEFT, // as long as this is not set it works
      style: map.ZoomControlStyle.SMALL,
    },

    mapTypeControlOptions: {
      position: map.ControlPosition.TOP_LEFT, // this makes the map type control disappear
    },
    fullscreenControl: false,
  };
}

const InfoModal = React.forwardRef(
  ({ longitude, latitude, courses, airports, images }: IProps, ref) => {
    const tabs = [
      {
        id: 1,
        label: "Photos",
      },
      {
        id: 2,
        label: "View on Map",
      },
    ];

    const [open, setOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(1);

    const images_array = images as Image[];

    if (!images || images_array.length === 0) {
      tabs.splice(1, 1);
    }

    React.useImperativeHandle(ref, () => ({
      open: (index: number) => {
        setOpen(true);
        setTabIndex(index);
      },
      close: () => {
        setOpen(false);
      },
    }));

    const getTabContent = () => {
      switch (tabIndex) {
        case 1:
          return (
            <div className="col-span-12 sm:p-5 lg:p-10 bg-white flex  justify-center">
              <div className="w-full ">
                <div className="grid  grid-cols-12 gap-2">
                  {images_array?.map((image, index) => {
                    // every 3rd image should be full width
                    let isFullWidth = index % 3 === 0;

                    let gridcss = isFullWidth
                      ? `col-span-12 sm:col-span-6 lg:col-span-4`
                      : `col-span-6 lg:col-span-4`;

                    return (
                      <div key={index} className={gridcss}>
                        <img
                          src={`${process.env.GATSBY_IMAGE_PREFIX}${image?.url}`}
                          alt={image.alt}
                          className="aspect-[4/3]  object-cover"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        case 2:
          return (
            <>
              <div className="col-span-12  bg-white">
                <div
                  style={{ height: "100%", width: "100%" }}
                  className="absolute right-0 bottom-0 top-0"
                >
                  <GoogleMapReact
                    options={mapOptionsCreator}
                    bootstrapURLKeys={{
                      key: "AIzaSyDol-lzNJaUNFDgWxBSFyDSiYKeZ9hj8Xk",
                    }}
                    defaultCenter={{
                      lat: parseFloat(latitude),
                      lng: parseFloat(longitude),
                    }}
                    defaultZoom={14}
                  >
                    {courses.map((course) => {
                      return (
                        <CourseMarker
                          key={course.id}
                          lat={course.latitude}
                          lng={course.longitude}
                          course={course}
                        />
                      );
                    })}
                    {airports?.map((airport, idx) => {
                      return (
                        <AirportMarker
                          key={idx}
                          airport={airport}
                          lat={airport.latitude}
                          lng={airport.longitude}
                        />
                      );
                    })}
                    <Marker lat={latitude} lng={longitude} />
                  </GoogleMapReact>
                </div>
              </div>
            </>
          );
      }
    };
    return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/80 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end sm:items-center justify-center min-h-full sm:p-4 text-center md:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="absolute bg-black sm:rounded-lg overflow-hidden shadow-xl transform transition-all sm:my-8 w-full h-full sm:w-[calc(100%-50px)] sm:h-[calc(100%-50px)]">
                  <div className="flex flex-col h-full">
                    <div className="bg-white flex pl-5 sm:pl-10 sm:pr-3 justify-between items-center border-b border-grey3">
                      <div className="inline-flex gap-[30px] sm:gap-[40px] overflow-x-scroll sm:overflow-auto noscrollbar ">
                        {tabs.map((tab, tInx) => {
                          let active = tab.id === tabIndex;
                          //is last tabIndex
                          let lastTab = tInx === tabs.length - 1;
                          return (
                            <div
                              key={tInx}
                              onClick={() => {
                                setTabIndex(tab.id);
                              }}
                            >
                              <Text
                                size={15}
                                leading={1.3}
                                weight={"semibold"}
                                color="black60"
                                className={`whitespace-nowrap border-b-[3px] cursor-pointer ${
                                  lastTab ? "mr-8" : ""
                                } ${
                                  active
                                    ? `!border-b-black !text-black !bg-white`
                                    : `border-b-white`
                                }   pt-5 pb-4`}
                              >
                                {tab.label}
                              </Text>
                            </div>
                          );
                        })}
                      </div>
                      <div
                        className="cursor-pointer bg-white px-4"
                        onClick={() => {
                          setOpen(false);
                        }}
                      >
                        <IoCloseOutline
                          className={"h-7 w-7 text-grey7"}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div className="flex flex-1 bg-white overflow-auto">
                      <div className="grid grid-cols-12 w-full text-left relative ">
                        {getTabContent()}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }
);

export default InfoModal;
